/**
 * Graph Explorer - Panels Module
 * Handles overlay panels for settings and context
 */
var __merlinGlobal = (typeof window !== 'undefined' ? window : globalThis);
var __merlinTrustedTypes = __merlinGlobal.MerlinTrustedTypes || (__merlinGlobal.MerlinTrustedTypes = (function() {
    function safeSetInnerHTML(element, html) {
        if (!element) return;
        if (!html) {
            element.replaceChildren();
            return;
        }

        var doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
        element.replaceChildren();
        Array.from(doc.body.childNodes).forEach(function(node) {
            element.appendChild(document.adoptNode(node));
        });
    }

    function safeInsertAdjacentHTML(element, position, html) {
        if (!element || !html) return;

        var doc = new DOMParser().parseFromString('<body>' + html + '</body>', 'text/html');
        var fragment = document.createDocumentFragment();
        Array.from(doc.body.childNodes).forEach(function(node) {
            fragment.appendChild(document.adoptNode(node));
        });

        switch (position) {
            case 'beforeend':
                element.appendChild(fragment);
                break;
            case 'afterbegin':
                element.prepend(fragment);
                break;
            case 'beforebegin':
                element.before(fragment);
                break;
            case 'afterend':
                element.after(fragment);
                break;
        }
    }

    return {
        safeSetInnerHTML: safeSetInnerHTML,
        safeInsertAdjacentHTML: safeInsertAdjacentHTML
    };
})());
var safeSetInnerHTML = __merlinTrustedTypes.safeSetInnerHTML;
var safeInsertAdjacentHTML = __merlinTrustedTypes.safeInsertAdjacentHTML;

(function(global) {
    'use strict';

    let currentPanel = null;
    let contextPanel = null;
    let backdrop = null;
    let contextPanelOpen = false;

    // ============================================
    // Panel Definitions
    // ============================================
    const panelConfigs = {
        color: {
            title: 'Color by',
            position: 'left',
            content: renderColorPanel
        },
        size: {
            title: 'Size Scale',
            position: 'left',
            content: renderSizePanel
        },
        filter: {
            title: 'Filter Nodes',
            position: 'left',
            content: renderFilterPanel
        },
        algorithm: {
            title: 'Run Algorithm',
            position: 'left',
            content: renderAlgorithmPanel
        },
        aliases: {
            title: 'Aliases',
            position: 'left',
            content: renderAliasesPanel
        },
        export: {
            title: 'Export',
            position: 'left',
            content: renderExportPanel
        },
        checkpoint: {
            title: 'Checkpoint',
            position: 'left',
            content: renderCheckpointPanel
        },
        context: {
            title: 'Context',
            position: 'right',
            content: renderContextPanel
        }
    };

    // ============================================
    // Initialization
    // ============================================
    function init() {
        createBackdrop();
        createPanelContainer();
        // Context panel replaced by ExplorerWindows
        bindEvents();
        
        console.log('[ExplorerPanels] Initialized');
    }

    function createBackdrop() {
        backdrop = document.getElementById('overlay-backdrop');
        if (!backdrop) {
            backdrop = document.createElement('div');
            backdrop.id = 'overlay-backdrop';
            backdrop.className = 'overlay-backdrop';
            document.querySelector('.graph-explorer')?.appendChild(backdrop);
        }
    }

    function createPanelContainer() {
        if (!document.getElementById('overlay-panel')) {
            const panel = document.createElement('div');
            panel.id = 'overlay-panel';
            panel.className = 'overlay-panel from-left';
            panel.setAttribute('role', 'dialog');
            panel.setAttribute('aria-modal', 'true');
            document.querySelector('.graph-explorer')?.appendChild(panel);
        }
    }

    function createContextPanel() {
        if (!document.getElementById('context-panel')) {
            contextPanel = document.createElement('div');
            contextPanel.id = 'context-panel';
            contextPanel.className = 'overlay-panel from-right';
            contextPanel.setAttribute('role', 'complementary');
            contextPanel.setAttribute('aria-label', 'Node context');
            safeSetInnerHTML(contextPanel, `
                <div class="context-panel-collapse" data-action="closeContext" title="Collapse panel">‹</div>
                <div class="panel-header">
                    <h3 class="panel-title">Node Details</h3>
                    <button class="panel-close" aria-label="Close panel" data-action="closeContext">×</button>
                </div>
                <div class="panel-content">
                    <div class="context-panel-empty">
                        <div class="icon">👆</div>
                        <div class="text">Double-click a node to see details</div>
                    </div>
                </div>
            `);
            document.querySelector('.graph-explorer')?.appendChild(contextPanel);
        } else {
            contextPanel = document.getElementById('context-panel');
        }
    }

    // ============================================
    // Panel Open/Close
    // ============================================
    function open(panelId, data = {}) {
        const config = panelConfigs[panelId];
        if (!config) {
            console.warn(`Panel "${panelId}" not found`);
            return;
        }

        // Context panel is handled separately
        if (panelId === 'context') {
            openContext(data);
            return;
        }

        const panel = document.getElementById('overlay-panel');
        if (!panel) return;

        // Update panel position class
        panel.className = `overlay-panel from-${config.position}`;
        
        // Render content
        safeSetInnerHTML(panel, `
            <div class="panel-header">
                <h3 class="panel-title">${config.title}</h3>
                <button class="panel-close" aria-label="Close panel" data-action="close">×</button>
            </div>
            <div class="panel-content">
                ${config.content(data)}
            </div>
        `);

        // Show panel and backdrop
        requestAnimationFrame(() => {
            backdrop?.classList.add('visible');
            panel.classList.add('open');
        });

        currentPanel = panelId;
        panel.setAttribute('aria-labelledby', 'panel-title');
        
        // Focus first focusable element
        const firstFocusable = panel.querySelector('button, input, select, [tabindex]:not([tabindex="-1"])');
        firstFocusable?.focus();

        ExplorerUtils.announceChange(`${config.title} panel opened`);
    }

    function close() {
        const panel = document.getElementById('overlay-panel');
        
        backdrop?.classList.remove('visible');
        panel?.classList.remove('open');
        
        currentPanel = null;
        ExplorerUtils.announceChange('Panel closed');
    }

    function isOpen() {
        return currentPanel !== null;
    }

    // ============================================
    // Context Panel (Right Side - Collapsible Sidebar)
    // ============================================
    function openContext(data = {}) {
        if (!contextPanel) return;

        const content = contextPanel.querySelector('.panel-content');
        const title = contextPanel.querySelector('.panel-title');
        
        if (content) {
            safeSetInnerHTML(content, renderContextPanel(data));
        }
        
        // Update title with node name if available
        if (title && data.node && (data.node.label || data.node.id)) {
            title.textContent = data.node.label || data.node.id;
        } else if (title) {
            title.textContent = 'Node Details';
        }

        contextPanel.classList.add('open');
        contextPanelOpen = true;
        
        // Update toggle button state
        const toggleBtn = document.getElementById('btn-context-toggle');
        if (toggleBtn) {
            toggleBtn.classList.add('active');
        }
    }

    function closeContext() {
        if (!contextPanel) return;

        contextPanel.classList.remove('open');
        contextPanelOpen = false;
        
        // Update toggle button state
        const toggleBtn = document.getElementById('btn-context-toggle');
        if (toggleBtn) {
            toggleBtn.classList.remove('active');
        }
    }

    function toggleContext() {
        if (contextPanelOpen) {
            closeContext();
        } else {
            openContext({});
        }
    }

    function isContextOpen() {
        return contextPanelOpen;
    }

    function updateContext(data) {
        if (!contextPanel || !contextPanelOpen) return;

        const content = contextPanel.querySelector('.panel-content');
        if (content) {
            safeSetInnerHTML(content, renderContextPanel(data));
        }
    }

    // ============================================
    // Panel Content Renderers
    // ============================================
    function renderColorPanel() {
        const options = [
            { value: 'domain', label: 'Domain (default)' },
            { value: 'resource_name', label: 'Resource Name' },
            { value: 'mixin_name', label: 'Mixin Name' },
            { value: 'taxonomy_name', label: 'Taxonomy Name' },
            { value: 'community_id', label: 'Community ID' }
        ];

        return `
            <div class="panel-section">
                <label class="panel-label">Color nodes by attribute:</label>
                <select class="panel-select" id="color-attribute-select" onchange="ExplorerPanels.handleColorChange(this.value)">
                    ${options.map(opt => `<option value="${opt.value}">${opt.label}</option>`).join('')}
                </select>
            </div>
        `;
    }

    function renderSizePanel() {
        const scales = [1, 2, 4, 8];
        const currentScale = GraphExplorer.getState().currentSizeScale || 2;

        return `
            <div class="panel-section">
                <label class="panel-label">Node size multiplier:</label>
                <div class="panel-btn-group">
                    ${scales.map(s => `
                        <button class="panel-btn${s === currentScale ? ' active' : ''}" 
                                onclick="ExplorerPanels.handleSizeChange(${s})">
                            x${s}
                        </button>
                    `).join('')}
                </div>
            </div>
        `;
    }

    function renderFilterPanel() {
        return `
            <div class="panel-section">
                <label class="panel-label">Filter by attribute:</label>
                <select class="panel-select" id="filter-attribute-select" onchange="ExplorerPanels.handleFilterAttributeChange(this.value)">
                    <option value="">Select attribute...</option>
                </select>
            </div>
            <div id="filter-controls" style="display: none;">
                <div class="panel-section">
                    <input type="range" class="panel-input" id="filter-range" min="0" max="100" value="50">
                    <div style="display: flex; justify-content: space-between; font-size: 10px; opacity: 0.6;">
                        <span id="filter-min">0</span>
                        <span id="filter-current">50</span>
                        <span id="filter-max">100</span>
                    </div>
                </div>
                <div class="panel-btn-group">
                    <button class="panel-btn" onclick="ExplorerPanels.applyFilter('hide')">Hide Below</button>
                    <button class="panel-btn" onclick="ExplorerPanels.applyFilter('dim')">Dim Below</button>
                </div>
            </div>
        `;
    }

    function renderAlgorithmPanel() {
        return `
            <div class="panel-section">
                <label class="panel-label">Select algorithm:</label>
                <select class="panel-select" id="algorithm-select">
                    <option value="louvain">LOUVAIN (Community Detection)</option>
                    <option value="pagerank">PageRank (Centrality)</option>
                    <option value="betweenness">Betweenness Centrality</option>
                </select>
            </div>
            <div class="panel-section" id="algorithm-params">
                <label class="panel-label">Resolution:</label>
                <input type="number" class="panel-input" id="algo-resolution" value="1.0" step="0.1" min="0.1" max="5">
            </div>
            <button class="panel-btn primary" onclick="ExplorerPanels.runAlgorithm()">
                ▶ Execute
            </button>
        `;
    }

    function renderAliasesPanel() {
        return `
            <div class="panel-section">
                <label class="panel-label">Available aliases:</label>
                <div id="aliases-list" style="max-height: 200px; overflow-y: auto;">
                    <p style="opacity: 0.5; font-size: 11px;">No aliases defined</p>
                </div>
            </div>
        `;
    }

    function renderExportPanel() {
        return `
            <div class="panel-section">
                <label class="panel-label">Export format:</label>
                <div class="panel-btn-group" style="flex-direction: column;">
                    <button class="panel-btn" onclick="ExplorerPanels.exportGraph('png')">
                        📷 Export as PNG
                    </button>
                    <button class="panel-btn" onclick="ExplorerPanels.exportGraph('json')">
                        📄 Export as JSON
                    </button>
                    <button class="panel-btn" onclick="ExplorerPanels.exportGraph('csv')">
                        📊 Export as CSV
                    </button>
                </div>
            </div>
        `;
    }

    function renderCheckpointPanel() {
        return `
            <div class="panel-section">
                <div class="panel-btn-group">
                    <button class="panel-btn" onclick="ExplorerPanels.saveCheckpoint()">
                        ◎ Save
                    </button>
                    <button class="panel-btn" onclick="ExplorerPanels.loadCheckpoint()">
                        ↺ Restore
                    </button>
                </div>
            </div>
            <div class="panel-section" id="checkpoint-info" style="opacity: 0.6; font-size: 11px;">
                No checkpoint saved
            </div>
        `;
    }

    function renderContextPanel(data = {}) {
        const node = data.node || {};
        const connections = data.connections || [];

        // If no node data, show empty state
        if (!node.id && !node.label) {
            return `
                <div class="context-panel-empty">
                    <div class="icon">👆</div>
                    <div class="text">Double-click a node to see details</div>
                </div>
            `;
        }

        return `
            <div class="context-panel">
                <h4 class="context-node-title">${node.label || node.id || 'Unknown Node'}</h4>
                
                <div class="context-meta">
                    <div class="context-meta-item">
                        <span class="context-meta-label">Domain</span>
                        <span class="context-meta-value">${node.domain || '-'}</span>
                    </div>
                    <div class="context-meta-item">
                        <span class="context-meta-label">Degree</span>
                        <span class="context-meta-value">${node.degree || connections.length || '-'}</span>
                    </div>
                    <div class="context-meta-item">
                        <span class="context-meta-label">Community</span>
                        <span class="context-meta-value">${node.community_id || '-'}</span>
                    </div>
                </div>
                
                <div class="context-connections">
                    <div class="context-connections-label">Connected to (${connections.length}):</div>
                    <div class="context-connections-list">
                        ${connections.length > 0 ? connections.slice(0, 8).map(c => `
                            <span class="context-connection-chip" onclick="ExplorerPanels.selectNode('${c.id}')">${c.label || c.id}</span>
                        `).join('') : '<span style="opacity: 0.5; font-size: 11px;">No connections</span>'}
                        ${connections.length > 8 ? `<span class="context-connection-chip">+${connections.length - 8} more</span>` : ''}
                    </div>
                </div>
                
                <div class="context-actions">
                    <button class="context-action-btn extend" onclick="ExplorerPanels.nodeAction('extend')">
                        <span>🔍</span>
                        <span>Extend</span>
                    </button>
                    <button class="context-action-btn connect" onclick="ExplorerPanels.nodeAction('connect')">
                        <span>🔗</span>
                        <span>Connect</span>
                    </button>
                    <button class="context-action-btn map" onclick="ExplorerPanels.nodeAction('map')">
                        <span>🗺️</span>
                        <span>Map</span>
                    </button>
                    <button class="context-action-btn delete" onclick="ExplorerPanels.nodeAction('delete')">
                        <span>🗑️</span>
                        <span>Delete</span>
                    </button>
                </div>
            </div>
        `;
    }

    // ============================================
    // Event Handlers
    // ============================================
    function bindEvents() {
        document.addEventListener('click', function(e) {
            // Close button (main panels)
            if (e.target.closest('[data-action="close"]')) {
                close();
            }
            
            // Close context button
            if (e.target.closest('[data-action="closeContext"]')) {
                closeContext();
            }
            
            // Context toggle button
            if (e.target.closest('#btn-context-toggle')) {
                toggleContext();
            }
            
            // Backdrop click
            if (e.target.classList.contains('overlay-backdrop')) {
                close();
            }
        });

        // Escape key
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
                if (isOpen()) {
                    close();
                } else if (isContextOpen()) {
                    closeContext();
                }
            }
        });
    }

    // ============================================
    // Action Handlers
    // ============================================
    function handleColorChange(attribute) {
        GraphExplorer.setColorByAttribute(attribute);
        // Update legend window if available
        if (typeof ExplorerWindows !== 'undefined' && ExplorerWindows.updateLegend) {
            ExplorerWindows.updateLegend();
        }
    }

    function handleSizeChange(scale) {
        GraphExplorer.setSizeScale(scale);
        // Update button states
        document.querySelectorAll('#overlay-panel .panel-btn-group .panel-btn').forEach(btn => {
            btn.classList.remove('active');
        });
        event.target.classList.add('active');
    }

    function handleFilterAttributeChange(attribute) {
        const controls = document.getElementById('filter-controls');
        if (controls) {
            controls.style.display = attribute ? 'block' : 'none';
        }
    }

    function applyFilter(mode) {
        // Implementation depends on existing filter logic
        console.log('Apply filter:', mode);
    }

    function runAlgorithm() {
        const algo = document.getElementById('algorithm-select')?.value;
        const resolution = parseFloat(document.getElementById('algo-resolution')?.value) || 1.0;
        console.log('Run algorithm:', algo, 'resolution:', resolution);
        // Send to API or execute locally
    }

    function exportGraph(format) {
        console.log('Export as:', format);
        // Implementation for export
    }

    function saveCheckpoint() {
        console.log('Save checkpoint');
        const info = document.getElementById('checkpoint-info');
        if (info) {
            info.textContent = `Saved: ${new Date().toLocaleTimeString()}`;
        }
    }

    function loadCheckpoint() {
        console.log('Load checkpoint');
        if (typeof resetToCheckpoint === 'function') {
            resetToCheckpoint();
        }
    }

    function selectNode(nodeId) {
        if (typeof network !== 'undefined') {
            network.selectNodes([nodeId]);
        }
    }

    function nodeAction(action) {
        const message = {
            type: 'pyvis_node_action',
            action: action,
            event: 'nodeAction'
        };
        window.parent.postMessage(message, '*');
    }

    // ============================================
    // Public API
    // ============================================
    global.ExplorerPanels = {
        init,
        open,
        close,
        isOpen,
        openContext,
        closeContext,
        toggleContext,
        isContextOpen,
        updateContext,
        handleColorChange,
        handleSizeChange,
        handleFilterAttributeChange,
        applyFilter,
        runAlgorithm,
        exportGraph,
        saveCheckpoint,
        loadCheckpoint,
        selectNode,
        nodeAction
    };

})(window);
